import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberService } from '../../core/services/member.service';

/**
 * 旧版 admin/views/transaction-edit.html + TransactionEditCtl (admin/js/controllers/transaction.js:715-745)
 * の独立した課金編集画面。一覧内モーダル(TransactionEditComponent)とは別ルート。
 * route: /transaction/:memberId/edit/:transactionId
 */
@Component({
    selector: 'app-transaction-edit-page',
    imports: [FormsModule, DecimalPipe],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="row sfapp-container">
    <div class="col-lg-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa fa-pencil"></i>&nbsp;課金編集</span>
        </div>
        <div class="card-body">
          @if (loading) {
            <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
          } @else {
            <form novalidate name="myEditForm" #myEditForm="ngForm" (ngSubmit)="save()">
              <div class="row">
                @if (successMsg) {
                  <div class="col-md-12 mb-2">
                    <div class="alert alert-success" role="alert">
                      <button type="button" class="close" (click)="successMsg=''" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                      {{ successMsg }}
                    </div>
                  </div>
                }
                @if (errorMsg) {
                  <div class="col-md-12 mb-2">
                    <div class="alert alert-danger" role="alert">
                      <button type="button" class="close" (click)="errorMsg=''" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                      {{ errorMsg }}
                    </div>
                  </div>
                }
                <div class="col-md-12">
                  <div class="form-group row">
                    <label class="col-md-2 col-form-label">店舗</label>
                    <label class="col-md-10 col-form-label">{{ transaction.studio?.name }}</label>
                  </div>
                  <div class="form-group row">
                    <label class="col-md-2 col-form-label">課金年月</label>
                    <label class="col-md-10 col-form-label">{{ transaction.yearPay }}年{{ transaction.monthPay }}月</label>
                  </div>
                  <div class="form-group row">
                    <label class="col-md-2 col-form-label">会員ID・会員氏名</label>
                    <label class="col-md-10 col-form-label">
                      {{ transaction.member?.memberCode }}&nbsp;&nbsp;
                      {{ transaction.member?.lastName }}&nbsp;{{ transaction.member?.firstName }}
                    </label>
                  </div>
                  <div class="form-group row">
                    <label class="col-md-2 col-form-label">支払方法</label>
                    @if (transaction.paymentMethod == 0) {
                      <label class="col-md-10 col-form-label">口座振替</label>
                    }
                    @if (transaction.paymentMethod == 1) {
                      <label class="col-md-10 col-form-label">クレジット</label>
                    }
                  </div>
                  <div class="form-group row">
                    <label class="col-md-2 col-form-label">課金タイプ</label>
                    <div class="col-md-10">
                      <select class="form-control" name="chargeType" [(ngModel)]="transaction.chargeType" required>
                        <option value="1">月会費</option>
                        <option value="2">入会金・事務手数料</option>
                        <option value="3">ロッカー費</option>
                        <option value="4">PT費</option>
                        <option value="0">その他</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-group row">
                    <label class="col-md-2 col-form-label">明細</label>
                    <div class="col-md-10">
                      <input type="text" class="form-control" name="title" [(ngModel)]="transaction.title" required>
                    </div>
                  </div>
                  <div class="form-group row">
                    <label class="col-md-2 col-form-label">金額</label>
                    @if (transaction.status == 0) {
                      <div class="col-md-10">
                        <input type="text" class="form-control" name="amount" [(ngModel)]="transaction.amount"
                               pattern="^-?\\d+$" required>
                      </div>
                    } @else {
                      <div class="col-md-10">
                        <span>¥{{ transaction.amount | number:'1.0-0' }}</span>
                      </div>
                    }
                  </div>
                </div>
                <div class="col-md-12 text-center">
                  <button type="submit" class="btn btn-outline-primary" [disabled]="saving || myEditForm.invalid">
                    <i class="fa fa-archive"></i>&nbsp;保存
                  </button>
                </div>
              </div>
            </form>
          }
        </div>
      </div>
    </div>
  </div>
</div>
  `
})
export class TransactionEditPageComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private memberService = inject(MemberService);
  private route = inject(ActivatedRoute);

  transaction: any = {};
  memberId = 0;
  transactionId = 0;
  loading = true;
  saving = false;
  successMsg = '';
  errorMsg = '';

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('決済管理', 's-icon-settlement');
    this.memberId = Number(this.route.snapshot.paramMap.get('memberId'));
    this.transactionId = Number(this.route.snapshot.paramMap.get('transactionId'));
    try {
      const data: any = await this.memberService.getOneTransaction(this.memberId, this.transactionId);
      this.setTransaction(data);
    } finally {
      this.loading = false;
    }
  }

  private setTransaction(data: any): void {
    this.transaction = data ?? {};
    // 旧版と同じく select の value に合わせて文字列化する
    this.transaction.chargeType = String(this.transaction.chargeType);
  }

  async save(): Promise<void> {
    this.successMsg = '';
    this.errorMsg = '';
    this.saving = true;
    try {
      // 旧版 transaction.js:729-737 と同じく更新対象外の項目を落として送信する
      const json: any = { ...this.transaction, id: this.transactionId };
      delete json.yearPay;
      delete json.monthPay;
      delete json.paymentMethod;
      delete json.status;
      delete json.member;
      delete json.studio;
      const data: any = await this.memberService.upsertTransaction(this.memberId, json);
      this.setTransaction({ ...this.transaction, ...(data ?? {}) });
      this.successMsg = 'データを更新しました';
    } catch {
      this.errorMsg = '更新に失敗しました';
    } finally {
      this.saving = false;
    }
  }
}
